import React, { HTMLAttributes, useCallback, useMemo, useState } from 'react';

import { WidgetProps } from '@rjsf/utils';
import { useSelector } from 'react-redux';

import { ListItemText, MenuItem } from '@mui/material';
import Autocomplete, { AutocompleteRenderOptionState } from '@mui/material/Autocomplete';
import TextField from '@mui/material/TextField';
import { debounce } from '@mui/material/utils';

import useToggleState from '~/hooks/useToggleState';

import SearchService, { TypeaheadBrand, TypeaheadResult, TypeaheadType } from '~/services/SearchService';

export const RELEVANT_TYPEAHEAD_TYPES: Array<TypeaheadType> = [
  'airport',
  'bedbank_property_unique_stays',
  'bedbank_property',
  'city',
  'colloquial_area',
  'continent',
  'country',
  'high_level_region',
  'le_property_unique_stays',
  'le_property',
  'metro_station',
  'multi_city_vicinity',
  'neighborhood',
  'point_of_interest',
  'province_state',
  'train_station',
  'le_experience',
  'channel_experience',
  'car_hire_location',
];

interface LocationOption {
  title: string;
  subtitle?: string;
  value: `[${string}] ${string}`;
  type?: string;
  typeahead?: TypeaheadResult;
}

function LocationMenuOption(
  props: HTMLAttributes<HTMLLIElement>,
  option: LocationOption,
  state: AutocompleteRenderOptionState,
) {
  return (
    <MenuItem {...props} selected={state.selected} dense disableGutters>
      <ListItemText primary={option.title} secondary={option.subtitle} />
    </MenuItem>
  );
}

interface Props extends WidgetProps {
  options: {
    typeaheadTypes?: Array<TypeaheadType>; // defaults to SearchService.KNOWN_TYPEAHEAD_TYPES
    typeaheadResultsLimit?: number; // defaults to 20
  };
}

function LELocationSearchWidget({ id, required, readonly, disabled, label, value, options, onChange }: Props) {
  const tenant = useSelector<App.State, App.Tenant>((state) => state.tenant);
  const { isToggled: isLoading, toggleOn: startLoading, toggleOff: endLoading } = useToggleState();
  const [typeaheadSuggestions, setTypeaheadSuggestions] = useState<Array<TypeaheadResult>>([]);

  const defaultLocation = useMemo<LocationOption | null>(() => {
    if (value) {
      const matches = value.match(/^\[(\S+)\]\s(.+)$/is);
      if (matches?.length) {
        const [, matchedValue, matchedLabel] = matches;
        return {
          value: matchedValue,
          title: matchedLabel,
        };
      } else {
        return {
          value,
          title: value,
        };
      }
    }
    return null;
  }, [value]);

  const locationOptions = useMemo<Array<LocationOption>>(
    () =>
      typeaheadSuggestions.map((typeahead) => {
        return {
          title: typeahead.format.mainText,
          subtitle: typeahead.format.secondaryText,
          value: `[${typeahead.id}] ${typeahead.format.mainText}, ${typeahead.format.secondaryText}`,
          type: typeahead.destinationType,
          typeahead,
        };
      }),
    [typeaheadSuggestions],
  );

  const fetchTypeahed = useCallback(
    (query) => {
      if (!query || query.length < 2 || isLoading) {
        return;
      }
      startLoading();
      SearchService.typeahead(query, {
        type: options?.typeaheadTypes ?? RELEVANT_TYPEAHEAD_TYPES,
        // FIXME: the tenant brand support "scooponexperience" but the typeahead does not.
        brand: (tenant?.brand as TypeaheadBrand) ?? 'luxuryescapes',
        region: 'AU',
        limit: options?.typeaheadResultsLimit ?? 20,
        priority: 'current',
      })
        .then((res) => setTypeaheadSuggestions(res.result))
        .catch((e) => {
          alert(`There was an error while fetching location typeahead [${JSON.stringify(e, null, 2)}]`);
        })
        .finally(endLoading);
    },
    [isLoading, options, tenant],
  );

  return (
    <Autocomplete
      id={id}
      multiple={false}
      value={defaultLocation}
      disabled={disabled}
      readOnly={readonly}
      options={locationOptions}
      getOptionLabel={(option: LocationOption) => option.title}
      groupBy={(option) => option.type}
      isOptionEqualToValue={(option, value) => option.value === value.value}
      filterOptions={(o) => o}
      loading={isLoading}
      loadingText="Looking around the 🌎"
      onChange={(e, newOption: LocationOption) => {
        onChange(newOption?.value ?? '');
      }}
      onInputChange={debounce((e, inputValue) => {
        setTypeaheadSuggestions([]);
        fetchTypeahed(inputValue);
      }, 300)}
      renderOption={LocationMenuOption}
      renderInput={(params) => (
        <TextField {...params} required={required} label={label} placeholder="Enter an LE location name" fullWidth />
      )}
    />
  );
}

export default LELocationSearchWidget;
